import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Row, Col, Spin, Button, Select, Input, Form, Popover, Checkbox, Tabs, DatePicker, Badge, Radio } from 'antd';

import { ReloadOutlined, ControlOutlined, SearchOutlined, FilterOutlined, CloseOutlined, ClearOutlined, FilterFilled } from '@ant-design/icons';

import PipelineOverviewConfigurator from '../../../shared/components/pipeline-overview-configurator/pipeline-overview-configurator';
import { pipelineDateFormat } from '../helpers/pipelines-overview-helper';
import { pipelinePagesTypes, taskPipelineCustomFilterTypes } from '../../../shared/constants/enums/pipelinePagesTypes';
import { getServiceRequestNames } from '../../../shared/constants/enums';
import { siteUrls } from '../../../shared/constants/siteUrls';
import { renderNoDataOrSpinner } from '../../../shared/utils';
import * as sharedActions from '../../../shared/store/actions';
import { serviceRequestStatusOpts } from '../../../shared/constants/lookupListConstants';
import '../styles.less';
import { userTypes } from '../../../shared/constants/enums';
import SelectWithFetchNext from '../../../shared/components/select-with-fetch-next';
import Filter from '../../../shared/components/filter';
import { filterTypes } from '../../../shared/constants/filterType';

const { RangePicker } = DatePicker;

let agentsFieldTimeout;

const OverviewHeader = ({
  columns,
  type,
  userInfo,
  fetchData,
  refresh,
  rootContainer,
  configuratorEnabled,
  saveControlConfiguration,
  updateColumnsState,
  linkToList,
  setIncludeRenewal,
  onPipelineTypeChange,
  refreshConstrolConfiguration,
  isLoading,
  isMomentumEnabled,
  canViewOnlyAssigned,
  statuses,
  customFiltersData,
}) => {
  const [popoverInitialized, setPopoverInitialized] = useState(false);
  const [filterPanelVisible, setFilterPanelVisible] = useState(false);
  const [configuratorVisible, setConfiguratorVisibility] = useState(false);
  const [agentsFieldVisible, setAgentsFieldVisibitity] = useState(false);
  const [agentsOptions, setAgentsOptions] = useState(null);
  const [assignedToOptions, setAssignedToOptions] = useState(null);

  const [filterForm] = Form.useForm();
  const dispatch = useDispatch();

  const agentsLookup = useSelector((state) => state.shared.agentsLookup);
  const agentsLookupLoading = useSelector((state) => state.shared.agentsLookupLoading);
  const userInfoLoading = useSelector((state) => state.shared.userInfoLoading);
  const insureds = useSelector((state) => state.shared.insureds);
  const insuredsLoading = useSelector((state) => state.shared.insuredsLoading);

  useEffect(() => {
    if (popoverInitialized) {
      dispatch(sharedActions.getAgentsLookup({ pageSize: 15, pageNumber: 1 }));
    }
  }, [popoverInitialized]);

  useEffect(() => {
    setAgentsOptions(agentsLookup);
  }, [agentsLookup]);

  useEffect(() => {
    if (!!userInfo) {
      const opts = [
        { key: 'all', value: 0, text: 'All' },
        { key: 'agents', value: 2, text: 'Assigned to ...' },
      ];

      if (userInfo.userType === userTypes.Agent) {
        opts.push({ key: 'me', value: 1, text: 'Assigned to me' });
      }

      setAssignedToOptions(opts);

      if (userInfo.userType === userTypes.Agent) {
        filterForm.setFieldsValue({ assignedTo: 1 });
      }
    }
  }, [userInfo]);

  const handleCloseModal = () => {
    setConfiguratorVisibility(false);
  };

  const handleSearchPopoverVisibleChange = (visible) => {
    if (visible && !popoverInitialized) {
      setPopoverInitialized(true);
    }
  };

  const isFilterApplied = () => {
    const assignedTo = filterForm.getFieldValue('assignedTo');

    if (!!assignedTo) {
      return true;
    }

    return false;
  };

  const handleAssignedToSelection = () => {
    if (popoverInitialized) {
      const formData = filterForm.getFieldsValue();

      if (!!formData.assignedTo && formData.assignedTo === 2) {
        // agents
        setAgentsFieldVisibitity(true);
      } else {
        setAgentsFieldVisibitity(false);
      }
    } else {
      setAgentsFieldVisibitity(false);
    }
  };

  const cleanToDefaultFormValues = () => {
    filterForm.resetFields();
    filterForm.setFieldValue('customFilter', taskPipelineCustomFilterTypes['AllTasks'].key);

    if (userInfo.userType === userTypes.Agent) {
      filterForm.setFieldValue('assignedTo', 1);
      filterForm.setFieldValue('agentIds', [userInfo.userId]);
    }
  };

  const clearFilters = () => {
    cleanToDefaultFormValues();
    setAgentsFieldVisibitity(false);
    handleSearchAgent(null);

    fetchData(true, getFilters());
  };

  const getHeader = (manualType) => {
    const chosenType = !manualType ? type : manualType;

    switch (chosenType) {
      case pipelinePagesTypes.RenewalOverview:
        return 'Renewal Opportunities';
      case pipelinePagesTypes.ServiceRequestsOverview:
        return 'Service Requests';
      case pipelinePagesTypes.ServiceRequestsStagesOverview:
        return 'Service Requests by stages';
      case pipelinePagesTypes.ServiceRequestsCustomTypesOverview:
        return 'Service Requests by custom type';
      case pipelinePagesTypes.EndorsementsOverview:
        return 'Endorsements';
      case pipelinePagesTypes.TaskOverview:
        return 'Tasks';
      case pipelinePagesTypes.OpportunitiesOverview:
        return 'Opportunities';
    }
  };

  const getSelectedItemsPlaceholder = (fieldName) => {
    const selectedItems = filterForm.getFieldValue(fieldName);
    if (selectedItems && selectedItems.length) {
      return `+${selectedItems.length - 1} Items`;
    }
  };

  const handleSearchAgent = (searchString) => {
    if (agentsFieldTimeout) {
      clearTimeout(agentsFieldTimeout);
      agentsFieldTimeout = null;
    }

    agentsFieldTimeout = setTimeout(() => dispatch(sharedActions.getAgentsLookup({ pageSize: 15, pageNumber: 1, searchString: searchString })), 500);
  };

  const applyFilters = (isCustomFilterApplied) => {
    if (!isCustomFilterApplied) {
      filterForm.setFieldValue('customFilter', null);
    }

    const appliedFilters = getFilters();

    fetchData(true, appliedFilters);

    setFilterPanelVisible(false);
  };

  const getFilters = () => {
    const formData = filterForm.getFieldsValue();

    if (formData.assignedTo === 1) {
      formData.agentIds = [userInfo.userId];
    }

    return formData;
  };

  const handleRefresh = () => {
    if (typeof refresh === 'function') {
      refresh(getFilters());
    }
  };

  const headerLabel = <span style={{ marginRight: '20px' }}>List of {getHeader()}</span>;

  const serviceRequestTabItems = [
    {
      key: pipelinePagesTypes.ServiceRequestsOverview,
      label: 'List of ' + getHeader(pipelinePagesTypes.ServiceRequestsOverview),
      disabled: isLoading,
    },
    {
      key: pipelinePagesTypes.ServiceRequestsStagesOverview,
      label: 'List of ' + getHeader(pipelinePagesTypes.ServiceRequestsStagesOverview),
      disabled: isLoading,
    },
    {
      key: pipelinePagesTypes.ServiceRequestsCustomTypesOverview,
      label: 'List of ' + getHeader(pipelinePagesTypes.ServiceRequestsCustomTypesOverview),
      disabled: isLoading,
    },
  ];

  const onChangeServiceRequestTab = (tabKey) => {
    if (typeof onPipelineTypeChange === 'function') {
      onPipelineTypeChange(tabKey);
    }
  };

  const onCustomFilterChange = (filterValue) => {
    cleanToDefaultFormValues();
    filterForm.setFieldValue('customFilter', filterValue);

    const dateTimeNow = moment().utc(true);

    switch (filterValue) {
      case taskPipelineCustomFilterTypes['AllTasks'].key:
        applyFilters(true);
        break;
      case taskPipelineCustomFilterTypes['DueToday'].key:
        filterForm.setFieldValue('dueDateRange', [dateTimeNow.clone().startOf('day'), dateTimeNow.clone().endOf('day')]);
        applyFilters(true);
        break;
      case taskPipelineCustomFilterTypes['Overdue'].key:
        const notCompletedStatuses = statuses
        .filter((statusObj) => statusObj.name !== 'Done' && statusObj.name !== 'Closed')
        ?.map((statusObj) => statusObj.value);
        
        if (notCompletedStatuses?.length) {
          filterForm.setFieldValue('dueDateRange', ['', dateTimeNow.clone().endOf('day').subtract(1, 'day')]);
          filterForm.setFieldValue('statusIds', notCompletedStatuses);
          applyFilters(true);
        }
        break;
      case taskPipelineCustomFilterTypes['RecentlyCompleted'].key:
        const completedStatuses = statuses
          .filter((statusObj) => statusObj.name === 'Done' || statusObj.name === 'Closed')
          ?.map((statusObj) => statusObj.value);

        if (completedStatuses?.length) {
          filterForm.setFieldValue('lastChangeDateRange', [dateTimeNow.clone().subtract(7, 'days'), dateTimeNow.clone()]);
          filterForm.setFieldValue('statusIds', completedStatuses);
          applyFilters(true);
        }
        break;
    }
  };

  const getInsuredFilter = () => (
    <Col span={12}>
      <Filter
        rootContainer={rootContainer}
        filterType={filterTypes.dropdownWithServerData}
        mode="multiple"
        name={'insuredIds'}
        placeholder="Type to search Insureds"
        data={insureds}
        dataLoading={insuredsLoading}
        dataSearchAction={(searchString) => sharedActions.getInsuredsByAgency(searchString)}
        keyPropertyName={'id'}
        textPropertyName={(insured) => {
          return insured.active ? insured.name : '(Inactive) ' + insured.name;
        }}
        maxTagCount={1}
      />
    </Col>
  );

  const getDueDateFilter = () => (
    <Col span={12}>
      <Form.Item name={['dueDateRange']} initialValue={['', '']}>
        <RangePicker
          allowEmpty={[true, true]}
          style={{ width: '100%' }}
          allowClear
          format={(date) => date.utc().format(pipelineDateFormat)}
          getPopupContainer={rootContainer}
          placeholder={['Due Date From:', 'Due Date To:']}
          ranges={{ today: [moment(), moment()] }}
        />
      </Form.Item>
    </Col>
  );

  return (
    <>
      <div className="overview-collapse-panel-header">
        <span>
          {type === pipelinePagesTypes.ServiceRequestsOverview ||
          type === pipelinePagesTypes.ServiceRequestsStagesOverview ||
          type === pipelinePagesTypes.ServiceRequestsCustomTypesOverview ? (
            <Tabs
              className="service-request-pipelines-tabs"
              defaultActiveKey={pipelinePagesTypes.ServiceRequestsOverview}
              disabled={isLoading}
              onChange={onChangeServiceRequestTab}
              items={serviceRequestTabItems}
            />
          ) : (
            headerLabel
          )}

          {type === pipelinePagesTypes.RenewalOverview || type === pipelinePagesTypes.OpportunitiesOverview ? (
            <Button ghost type="primary" size="small" style={{ marginRight: '10px' }} href={siteUrls.getCreateOpportunityUrl(isMomentumEnabled)}>
              Add new
            </Button>
          ) : null}

          {!!linkToList ? (
            <Button ghost type="primary" size="small" style={{ marginRight: '10px' }} href={linkToList}>
              Go to list
            </Button>
          ) : null}

          {type === pipelinePagesTypes.OpportunitiesOverview ? (
            <Checkbox size="small" style={{ marginRight: '10px' }} onChange={(val) => setIncludeRenewal(val.target.checked)}>
              Include Renewals
            </Checkbox>
          ) : null}

          {type === pipelinePagesTypes.TaskOverview && customFiltersData ? (
            <>
              <Radio.Group
                value={customFiltersData?.customFilter}
                onChange={(e) => onCustomFilterChange(e.target.value)}
                size="small"
                style={{
                  marginTop: 0,
                }}
              >
                {Object.values(taskPipelineCustomFilterTypes).map((obj) => {
                  return (
                    <Radio.Button
                      loading={isLoading}
                      checked={customFiltersData?.customFilter === obj.key}
                      size="small"
                      key={'task-pipeline-radiobutton-filter-' + obj.key}
                      title={obj.label}
                      value={obj.key}
                    >
                      {obj.label}
                      {obj.key === customFiltersData?.customFilter && customFiltersData?.totalItems > 0 && !isLoading
                        ? ' (' + customFiltersData.totalItems + ')'
                        : ''}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
            </>
          ) : null}
        </span>

        <span>
          <Button type="link" title="Refresh" icon={<ReloadOutlined />} onClick={() => handleRefresh()} />
          {configuratorEnabled ? (
            <Button
              type="link"
              title={columns ? 'Configurations' : 'No Data Available'}
              icon={
                columns ||
                (!isLoading &&
                  (pipelinePagesTypes.ServiceRequestsStagesOverview === type || pipelinePagesTypes.ServiceRequestsCustomTypesOverview === type)) ? (
                  <ControlOutlined />
                ) : (
                  <Spin />
                )
              }
              onClick={() => setConfiguratorVisibility(true)}
            />
          ) : null}
          {/* todo: this button hidden because export to excel not implemented yet. */}
          {/* <Button type="link" title="Export to Excel" icon={<FileExcelOutlined />} /> */}

          <Popover
            forceRender
            overlayStyle={{ width: '680px' }}
            onOpenChange={(e) => {
              handleSearchPopoverVisibleChange(e);
            }}
            content={
              <Form layout="horizontal" size="small" className="header-filter-form" form={filterForm}>
                {!canViewOnlyAssigned ? (
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Form.Item name={['assignedTo']} wrapperCol={{ sm: 24 }}>
                        <Select
                          showArrow
                          placeholder="Choose assignee"
                          getPopupContainer={rootContainer}
                          optionFilterProp="children"
                          onSelect={handleAssignedToSelection}
                          loading={userInfoLoading}
                          filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                          {assignedToOptions
                            ? assignedToOptions.map((opt) => {
                                return (
                                  <Select.Option key={opt.key} title={opt.text} value={opt.value}>
                                    {opt.text}
                                  </Select.Option>
                                );
                              })
                            : null}
                        </Select>
                      </Form.Item>
                    </Col>
                    {agentsFieldVisible ? (
                      <Col span={12}>
                        <Form.Item name={['agentIds']} wrapperCol={{ sm: 24 }} initialValue={[]}>
                          <Select
                            allowClear
                            showArrow
                            showSearch
                            mode="multiple"
                            maxTagPlaceholder={() => getSelectedItemsPlaceholder('agentIds')}
                            tokenSeparators={[,]}
                            maxTagCount={1}
                            loading={agentsLookupLoading || userInfoLoading}
                            getPopupContainer={rootContainer}
                            placeholder="Type to search Agents"
                            filterOption={false}
                            onSearch={handleSearchAgent}
                            notFoundContent={renderNoDataOrSpinner(agentsLookupLoading || userInfoLoading)}
                          >
                            {agentsOptions && agentsOptions.dataList.length
                              ? agentsOptions.dataList.map((agent) => (
                                  <Select.Option value={agent.key} key={agent.key}>
                                    {agent.value}
                                  </Select.Option>
                                ))
                              : null}
                          </Select>
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                ) : null}

                {type === pipelinePagesTypes.ServiceRequestsOverview ||
                type === pipelinePagesTypes.ServiceRequestsStagesOverview ||
                type === pipelinePagesTypes.ServiceRequestsCustomTypesOverview ? (
                  <>
                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Form.Item name={['serviceRequestTypes']} wrapperCol={{ sm: 24 }}>
                          <Select
                            showArrow
                            placeholder="Choose type"
                            mode="multiple"
                            getPopupContainer={rootContainer}
                            optionFilterProp="children"
                            loading={userInfoLoading}
                            filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                          >
                            {getServiceRequestNames.map((opt) => {
                              return (
                                <Select.Option key={opt.key} title={opt.name} value={opt.key}>
                                  {opt.name}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name={['subject']} wrapperCol={{ sm: 24 }} initialValue={''}>
                          <Input placeholder="Enter subject" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 8]}>
                      {type !== pipelinePagesTypes.ServiceRequestsOverview ? (
                        <Col span={12}>
                          <Form.Item name={['status']} wrapperCol={{ sm: 24 }}>
                            <Select
                              showArrow
                              placeholder="Choose status"
                              getPopupContainer={rootContainer}
                              optionFilterProp="children"
                              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                            >
                              {serviceRequestStatusOpts.map((opt) => {
                                return (
                                  <Select.Option key={opt.key} title={opt.text} value={opt.value}>
                                    {opt.text}
                                  </Select.Option>
                                );
                              })}
                            </Select>
                          </Form.Item>
                        </Col>
                      ) : null}
                      {type !== pipelinePagesTypes.ServiceRequestsStagesOverview ? (
                        <Col span={12}>
                          <Form.Item name={['stage']} wrapperCol={{ sm: 24 }} initialValue={''}>
                            <Input placeholder="Enter stage" />
                          </Form.Item>
                        </Col>
                      ) : null}
                      {type !== pipelinePagesTypes.ServiceRequestsCustomTypesOverview ? (
                        <Col span={12}>
                          <Form.Item name={['customType']} wrapperCol={{ sm: 24 }} initialValue={''}>
                            <Input placeholder="Enter custom type" />
                          </Form.Item>
                        </Col>
                      ) : null}
                    </Row>
                  </>
                ) : null}

                {type === pipelinePagesTypes.TaskOverview ? (
                  <>
                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Form.Item name={['ticketTitle']} wrapperCol={{ sm: 24 }} initialValue={''}>
                          <Input placeholder="Enter title" />
                        </Form.Item>
                      </Col>
                      {getDueDateFilter()}
                    </Row>

                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <SelectWithFetchNext
                          rootContainer={rootContainer}
                          dropdownMatchSelectWidth={false}
                          fetchNext={sharedActions.getTags}
                          placeholder="Type to search Tags"
                          multipleMode
                          enableSelectAll
                          formItemName={'tagIds'}
                          putInFormItem
                          hideFormLabel
                          customRender={(tags) => {
                            return tags && tags.length
                              ? tags.map((tagCategory) => {
                                  return (
                                    <Select.OptGroup key={tagCategory.id} label={tagCategory.name}>
                                      {tagCategory.tags
                                        ? tagCategory.tags.map((tag) => (
                                            <Select.Option value={tag.id} key={tag.id} item_object={{ ...tag, key: tag.id }}>
                                              <Badge color={tag.color} /> {tag.name}
                                            </Select.Option>
                                          ))
                                        : null}
                                    </Select.OptGroup>
                                  );
                                })
                              : null;
                          }}
                        />
                      </Col>
                      {getInsuredFilter()}
                    </Row>

                    <Row gutter={[16, 8]}>
                      {statuses?.length ? (
                        <Col span={12}>
                          <Form.Item name={['statusIds']} wrapperCol={{ sm: 24 }}>
                            <Select
                              showArrow
                              placeholder="Choose stutus"
                              mode="multiple"
                              getPopupContainer={rootContainer}
                              optionFilterProp="children"
                              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                            >
                              {statuses.map((opt) => {
                                return (
                                  <Select.Option key={'task-status-value-' + opt.value} title={opt.name} value={opt.value}>
                                    {opt.name}
                                  </Select.Option>
                                );
                              })}
                            </Select>
                          </Form.Item>
                        </Col>
                      ) : null}
                      <Col span={12}>
                        <Form.Item name={['lastChangeDateRange']} initialValue={['', '']}>
                          <RangePicker
                            allowEmpty={[true, true]}
                            style={{ width: '100%' }}
                            allowClear
                            format={(date) => date.utc().format(pipelineDateFormat)}
                            getPopupContainer={rootContainer}
                            placeholder={['Last Change Date From:', 'Last Change Date To:']}
                            ranges={{ today: [moment(), moment()] }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item hidden={true} name={['customFilter']} initialValue={[customFiltersData?.customFilter]}>
                      <Input />
                    </Form.Item>
                  </>
                ) : (
                  <Row gutter={[16, 8]}>
                    {getInsuredFilter()}
                    {type === pipelinePagesTypes.OpportunitiesOverview || type == pipelinePagesTypes.RenewalOverview ? (
                      <>{getDueDateFilter()}</>
                    ) : null}
                  </Row>
                )}

                <Row gutter={[8, 0]} justify="end">
                  <Col
                    span={10}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      type="primary"
                      ghost
                      icon={<SearchOutlined />}
                      title="Apply Search"
                      onClick={() => applyFilters(false)}
                      style={{ marginRight: '8px' }}
                      // loading={loading}
                    >
                      Search
                    </Button>

                    <Button
                      type="primary"
                      ghost
                      icon={<ClearOutlined />}
                      title="Reset Filters"
                      onClick={clearFilters}
                      style={{ marginRight: '8px' }}
                      // loading={loading}
                    >
                      Reset
                    </Button>

                    <Button type="primary" ghost icon={<CloseOutlined />} title="Close Filter Panel" onClick={() => setFilterPanelVisible(false)}>
                      Close
                    </Button>
                  </Col>
                </Row>
              </Form>
            }
            title="Filter"
            trigger="click"
            open={filterPanelVisible}
            getPopupContainer={rootContainer}
            placement="bottomRight"
          >
            <Button
              type="link"
              icon={isFilterApplied() ? <FilterFilled /> : <FilterOutlined />}
              title="Filter"
              onClick={() => setFilterPanelVisible(!filterPanelVisible)}
            />
          </Popover>
        </span>
      </div>

      {configuratorVisible ? (
        <PipelineOverviewConfigurator
          rootContainer={rootContainer}
          saveControlConfiguration={saveControlConfiguration}
          closeModal={handleCloseModal}
          columns={columns}
          updateColumnsState={updateColumnsState}
          refreshConstrolConfiguration={refreshConstrolConfiguration}
          serviceRequestType={type}
        />
      ) : null}
    </>
  );
};

export default OverviewHeader;
